// Patient profile → Calls tab.
// Lists every call (incoming/outgoing) tied to this patient via CallLog.
// Source: GET /api/incoming-calls/by-patient/:patientId

import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Phone, PhoneIncoming, PhoneOutgoing, PhoneMissed } from 'lucide-react';

export default function PatientCallHistory({ patientId, API_BASE }) {
  const [calls, setCalls] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!patientId) return;
    let cancelled = false;
    setLoading(true);
    axios.get(`${API_BASE}/incoming-calls/by-patient/${patientId}`)
      .then((r) => { if (!cancelled) setCalls(Array.isArray(r.data) ? r.data : []); })
      .catch((e) => { if (!cancelled) setError(e?.response?.data?.error || e.message); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [patientId, API_BASE]);

  if (loading) return <div style={{ padding: 20, color: '#6b7785' }}>Loading call history…</div>;
  if (error)   return <div style={{ padding: 20, color: '#b91c1c' }}>Error: {error}</div>;

  if (!calls.length) {
    return (
      <div style={emptyStyle}>
        <Phone size={32} color="#cbd5e1" />
        <div style={{ marginTop: 10, color: '#6b7785', fontSize: 14 }}>
          No call history yet. Calls will appear here automatically when the
          mobile call detector or cloud-telephony webhook tags this patient.
        </div>
      </div>
    );
  }

  return (
    <div>
      <div style={{ marginBottom: 12, color: '#6b7785', fontSize: 13 }}>
        {calls.length} call{calls.length === 1 ? '' : 's'} on record
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {calls.map((c) => <CallRow key={c._id} call={c} />)}
      </div>
    </div>
  );
}

function CallRow({ call }) {
  const isIncoming = call.direction === 'inbound';
  const isMissed = (call.outcome === 'missed') || (isIncoming && (call.durationSec || 0) === 0);
  const Icon = isMissed ? PhoneMissed : isIncoming ? PhoneIncoming : PhoneOutgoing;
  const color = isMissed ? '#dc2626' : isIncoming ? '#10b981' : '#3b82f6';
  const number = call.fromNumber || call.toNumber || '—';
  const when = call.createdAt ? new Date(call.createdAt) : null;
  const dur  = formatDuration(call.durationSec);

  return (
    <div style={rowStyle}>
      <div style={{ ...iconWrapStyle, background: color + '15' }}>
        <Icon size={18} color={color} />
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 14, fontWeight: 700, color: '#0f172a' }}>
          {number}
          {isMissed && <span style={{ marginLeft: 8, fontSize: 11, color: '#dc2626', fontWeight: 600 }}>MISSED</span>}
        </div>
        <div style={{ fontSize: 12, color: '#6b7785', marginTop: 2 }}>
          {when ? when.toLocaleString() : '—'} · {isIncoming ? 'Incoming' : 'Outgoing'}
          {!isMissed && <span> · {dur}</span>}
          {call.handledBy ? <span> · by {call.handledBy}</span> : null}
        </div>
        {call.staffNotes ? (
          <div style={{ fontSize: 12, color: '#475569', marginTop: 4, fontStyle: 'italic' }}>
            "{call.staffNotes}"
          </div>
        ) : null}
      </div>
      {call.outcome && call.outcome !== 'other' && (
        <span style={outcomeBadge(call.outcome)}>{call.outcome}</span>
      )}
    </div>
  );
}

function formatDuration(sec) {
  const s = Number(sec) || 0;
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  const r = s % 60;
  return `${m}m ${r}s`;
}

const rowStyle = {
  display: 'flex', alignItems: 'flex-start', gap: 10,
  padding: 12, borderRadius: 10, background: '#fff',
  border: '1px solid #e5e9f0',
};
const iconWrapStyle = {
  width: 36, height: 36, borderRadius: 18,
  display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
};
const emptyStyle = {
  padding: 40, textAlign: 'center', background: '#fff',
  borderRadius: 12, border: '1px dashed #e5e9f0',
};
const outcomeBadge = (outcome) => ({
  fontSize: 10, fontWeight: 700, textTransform: 'uppercase',
  padding: '4px 8px', borderRadius: 4,
  background: outcome === 'booked' ? '#dcfce7' : outcome === 'missed' ? '#fee2e2' : '#e0e7ff',
  color:      outcome === 'booked' ? '#15803d' : outcome === 'missed' ? '#991b1b' : '#3730a3',
  alignSelf: 'flex-start',
});
